/* eslint-disable @typescript-eslint/no-explicit-any */
import { withChildren } from '@builder.io/react';
import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import Section from 'molecules/Section';
import TableOfContents from 'molecules/TableOfContents';

import AuthorCard from 'organisms/Cards/AuthorCard';
import CompanyCard from 'organisms/Cards/Company';
import ResourceConversionPanel from 'organisms/Cards/ResourceConversionPanel';

import ContainedConversionPanel from 'components/ContainedConversionPanel';

import { ArticleConfigProps } from 'layout/article/config';
import useArticleHeadings from 'layout/article/utils/useArticleHeadings';

import { getBuilderStateData } from 'utils/builder';
import { trackLockReached, trackScrollDepth, trackUnlockSuccess } from 'utils/locked/analytics';
import { getUnlockStatus, saveUnlockStatus } from 'utils/locked/unlockState';

interface ArticleProps extends ArticleConfigProps {
  builderState: any;
}

const looksLikeAuthor = (a: any) => a && typeof a === 'object' && (a.name || a.title || (a.avatar && a.avatar.url));

const Article: FC<ArticleProps> = ({
  children,
  conversionPanel,
  company,
  tableOfContents,
  tocHeading,
  showProgressBar = false,
  enableLockedContent = false,
  pageId,
  lockHeadlineId,
  headingCutDivisor = 1.4,
  lockPercentage = 25,
  useSeparatePercentages = false,
  lockPercentageMobile = 25,
  lockPercentageDesktop = 25,
  lockHeading = "You've read 25% of this guide",
  lockDescription = 'Enter your work email to unlock the full guide and continue reading.',
  unlockForm,
  builderState,
  resourceConversionPanelTheme,
}) => {
  const router = useRouter();
  const activePath = router.asPath.split('?')[0].split('#')[0];
  const articleRef = useRef<HTMLDivElement>(null);
  const authorCardRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const lockBannerRef = useRef<HTMLDivElement>(null);

  const [authorData, setAuthorData] = useState<AuthorReference | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const rafRef = useRef<number | null>(null);
  const lastScrollY = useRef(0);

  // Locked content state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [hasReachedLock, setHasReachedLock] = useState(false);
  const [clipHeight, setClipHeight] = useState<number | null>(null);
  const [lockPosition, setLockPosition] = useState<number>(0);
  const scrollMilestonesRef = useRef(new Set<number>());
  const [showLockBanner, setShowLockBanner] = useState(false);

  // Determine effective lock percentage based on screen size
  const getEffectiveLockPercentage = () => {
    if (!useSeparatePercentages) {
      return lockPercentage;
    }
    const isMobile = window.innerWidth < 768;

    return isMobile ? lockPercentageMobile : lockPercentageDesktop;
  };

  useEffect(() => {
    const a = getBuilderStateData('author', builderState);
    if (looksLikeAuthor(a)) {
      setAuthorData(a);
    }
  }, [builderState]);

  useEffect(() => {
    if (enableLockedContent && !isUnlocked && unlockForm) {
      const timer = setTimeout(() => {
        setShowLockBanner(true);
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [enableLockedContent, isUnlocked, unlockForm]);

  useArticleHeadings(children, articleRef);

  // Check localStorage for unlock status
  useEffect(() => {
    if (!enableLockedContent || !pageId) {
      return;
    }

    const unlockData = getUnlockStatus(pageId);
    if (unlockData?.isUnlocked) {
      setIsUnlocked(true);
    }
  }, [enableLockedContent, pageId]);

  // Hide specific components by ID when locked
  useEffect(() => {
    if (!enableLockedContent || isUnlocked) {
      return;
    }

    const hideComponents = () => {
      const allElements = document.querySelectorAll('[id]');

      allElements.forEach(element => {
        const id = element.id.toLowerCase();
        if (id.includes('hidden')) {
          (element as HTMLElement).style.setProperty('display', 'none', 'important');
          (element as HTMLElement).style.setProperty('height', '0', 'important');
          (element as HTMLElement).style.setProperty('margin', '0', 'important');
          (element as HTMLElement).style.setProperty('padding', '0', 'important');
          (element as HTMLElement).style.setProperty('overflow', 'hidden', 'important');
        }
      });
    };

    const t1 = window.setTimeout(hideComponents, 100);
    const t2 = window.setTimeout(hideComponents, 500);
    const t3 = window.setTimeout(hideComponents, 1000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);

      const allElements = document.querySelectorAll('[id]');
      allElements.forEach(element => {
        const id = element.id.toLowerCase();
        if (id.includes('hidden')) {
          (element as HTMLElement).style.removeProperty('display');
          (element as HTMLElement).style.removeProperty('height');
          (element as HTMLElement).style.removeProperty('margin');
          (element as HTMLElement).style.removeProperty('padding');
          (element as HTMLElement).style.removeProperty('overflow');
        }
      });
    };
  }, [enableLockedContent, isUnlocked]);

  // Calculate clip height and lock position
  useEffect(() => {
    if (!enableLockedContent || !contentWrapperRef.current || !articleRef.current) {
      return;
    }

    const calculateClipHeight = () => {
      if (!contentWrapperRef.current || !articleRef.current) {
        return;
      }

      let lockOffset: number;
      let lockPos: number;

      // Get absolute positions using getBoundingClientRect
      const wrapperRect = contentWrapperRef.current.getBoundingClientRect();
      const wrapperTop = wrapperRect.top + window.scrollY;
      const articleRect = articleRef.current.getBoundingClientRect();
      const articleTop = articleRect.top + window.scrollY;

      if (lockHeadlineId) {
        const targetHeading = document.getElementById(lockHeadlineId);
        if (targetHeading) {
          const headingTop = targetHeading.getBoundingClientRect().top + window.scrollY;
          const headingHeight = targetHeading.offsetHeight;
          // Use configurable divisor to control where heading is cut
          lockOffset = headingTop - wrapperTop + headingHeight / headingCutDivisor;
          lockPos = headingTop;
        } else {
          console.warn(`[Locked Content] Headline with ID "${lockHeadlineId}" not found. Falling back to percentage.`);
          // Calculate based on article height using absolute positions
          const articleHeight = articleRef.current.scrollHeight;
          const effectivePercentage = getEffectiveLockPercentage();
          const articleLockOffset = (articleHeight * effectivePercentage) / 100;
          // Distance from wrapper top to lock position
          lockOffset = articleTop - wrapperTop + articleLockOffset;
          lockPos = articleTop + articleLockOffset;
        }
      } else {
        // Calculate based on article height using absolute positions (consistent across all resolutions)
        const articleHeight = articleRef.current.scrollHeight;
        const effectivePercentage = getEffectiveLockPercentage();
        const articleLockOffset = (articleHeight * effectivePercentage) / 100;
        // Distance from wrapper top to lock position
        lockOffset = articleTop - wrapperTop + articleLockOffset;
        lockPos = articleTop + articleLockOffset;
      }

      setClipHeight(lockOffset);
      setLockPosition(lockPos);
    };

    const t1 = window.setTimeout(calculateClipHeight, 100);
    const t2 = window.setTimeout(calculateClipHeight, 500);
    const t3 = window.setTimeout(calculateClipHeight, 1000);
    const t4 = window.setTimeout(calculateClipHeight, 2000);

    window.addEventListener('resize', calculateClipHeight);
    window.addEventListener('load', calculateClipHeight);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      window.removeEventListener('resize', calculateClipHeight);
      window.removeEventListener('load', calculateClipHeight);
    };
  }, [
    enableLockedContent,
    lockHeadlineId,
    lockPercentage,
    useSeparatePercentages,
    lockPercentageMobile,
    lockPercentageDesktop,
    children,
  ]);

  // Intercept TOC clicks when content is locked
  useEffect(() => {
    if (!enableLockedContent || isUnlocked) {
      return;
    }

    const handleTocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href^="#"]');
      if (!link) {
        return;
      }

      const href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      if (!targetElement) {
        return;
      }

      // Use absolute position for consistency
      const targetTop = targetElement.getBoundingClientRect().top + window.scrollY;

      if (targetTop > lockPosition) {
        e.preventDefault();
        e.stopPropagation();

        if (lockBannerRef.current) {
          const yOffset = window.innerWidth >= 1280 ? -80 : -72;
          const targetPosition = lockBannerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          const startPosition = window.pageYOffset;
          const distance = targetPosition - startPosition;
          const duration = 1800;
          let startTime: number | null = null;

          const easeInOutQuart = (t: number): number => (t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2);

          const animation = (currentTime: number) => {
            if (startTime === null) {
              startTime = currentTime;
            }
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const ease = easeInOutQuart(progress);

            window.scrollTo(0, startPosition + distance * ease);

            if (timeElapsed < duration) {
              requestAnimationFrame(animation);
            }
          };

          requestAnimationFrame(animation);
        }
      }
    };

    document.addEventListener('click', handleTocClick, true);

    return () => document.removeEventListener('click', handleTocClick, true);
  }, [enableLockedContent, isUnlocked, lockPosition]);

  const handleUnlockCallback = useCallback(
    (ctx?: { values?: Record<string, any> }) => {
      if (!pageId) {
        console.error('[Locked Content] No pageId provided');

        return false;
      }

      try {
        const values = ctx?.values || {};
        const email = values.Email || values.email;

        if (!email) {
          console.error('[Locked Content] No email found in success values:', values);

          return false;
        }

        saveUnlockStatus(pageId, String(email));
        setIsUnlocked(true);

        const effectivePercentage = getEffectiveLockPercentage();

        trackUnlockSuccess({
          pageId,
          email: String(email),
          lockPercentage: effectivePercentage,
        });

        return false;
      } catch (error) {
        console.error('[Locked Content] Error in unlock callback:', error);

        return false;
      }
    },
    [pageId, lockPercentage, useSeparatePercentages, lockPercentageMobile, lockPercentageDesktop],
  );

  // Progress bar tracking
  useEffect(() => {
    if (!showProgressBar) {
      return;
    }

    const calculateProgress = () => {
      if (!articleRef.current) {
        return;
      }

      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      const articleRect = articleRef.current.getBoundingClientRect();
      const articleTop = articleRect.top + scrollY;
      const articleHeight = articleRef.current.offsetHeight;

      const scrollStart = articleTop - 80;

      let scrollEnd: number;
      if (authorCardRef.current) {
        const authorRect = authorCardRef.current.getBoundingClientRect();
        const authorBottom = authorRect.top + scrollY + authorCardRef.current.offsetHeight;
        scrollEnd = authorBottom - windowHeight;
      } else {
        scrollEnd = articleTop + articleHeight - windowHeight;
      }

      const scrollRange = scrollEnd - scrollStart;

      if (scrollY <= scrollStart) {
        setScrollProgress(0);
        setIsVisible(false);
      } else if (scrollY >= scrollEnd) {
        setScrollProgress(100);
        setIsVisible(true);
      } else {
        const progress = ((scrollY - scrollStart) / scrollRange) * 100;
        setScrollProgress(Math.min(Math.max(progress, 0), 100));
        setIsVisible(true);
      }

      lastScrollY.current = scrollY;
    };

    const handleScroll = () => {
      if (Math.abs(window.scrollY - lastScrollY.current) > 5) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(calculateProgress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const timer = window.setTimeout(calculateProgress, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [showProgressBar, authorData]);

  // Locked content scroll tracking
  useEffect(() => {
    if (!enableLockedContent || !pageId || !articleRef.current) {
      return;
    }

    const handleLockedScroll = () => {
      if (!articleRef.current) {
        return;
      }

      const scrollY = window.scrollY;
      // Use absolute position for consistency across resolutions
      const articleTop = articleRef.current.getBoundingClientRect().top + window.scrollY;
      const articleHeight = articleRef.current.scrollHeight;

      // Calculate scroll percentage based on article content only
      const scrolled = scrollY - articleTop;
      const scrollPercentage = (scrolled / articleHeight) * 100;

      const milestones = [25, 50, 75, 90];
      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !scrollMilestonesRef.current.has(milestone)) {
          scrollMilestonesRef.current.add(milestone);
          trackScrollDepth({
            pageId,
            scrollProgress: milestone,
          });
        }
      });

      const effectivePercentage = getEffectiveLockPercentage();

      if (!hasReachedLock && scrollPercentage >= effectivePercentage) {
        setHasReachedLock(true);
        trackLockReached({
          pageId,
          lockPercentage: effectivePercentage,
          scrollProgress: scrollPercentage,
        });
      }
    };

    window.addEventListener('scroll', handleLockedScroll, { passive: true });
    handleLockedScroll();

    return () => window.removeEventListener('scroll', handleLockedScroll);
  }, [
    enableLockedContent,
    pageId,
    lockPercentage,
    useSeparatePercentages,
    lockPercentageMobile,
    lockPercentageDesktop,
    hasReachedLock,
  ]);

  const [showConversionPanel, setShowConversionPanel] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowConversionPanel(true), 500);

    return () => clearTimeout(timer);
  }, []);

  const toPlainObject = (maybeMapOrObj: unknown) => {
    if (maybeMapOrObj && typeof maybeMapOrObj === 'object') {
      if (typeof (maybeMapOrObj as any).entries === 'function') {
        return Object.fromEntries((maybeMapOrObj as Map<string, unknown>).entries());
      }

      return maybeMapOrObj;
    }

    return null;
  };

  const normalizeTocItem = (raw: any, index: number) => {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const id = raw.id ?? `contents-${index}`;
    const label = raw.label ?? 'Heading';
    const link = raw.link || '#';

    return {
      id,
      label,
      link,
      scrollLink: raw.scrollLink || undefined,
    };
  };

  const normalizeToc = (toc: unknown) => {
    try {
      if (!toc) {
        return [];
      }

      let items: unknown[] = [];

      if (Array.isArray(toc)) {
        items = toc;
      } else if (typeof toc === 'string') {
        const s = toc.trim();
        if (!(s.startsWith('[') || s.startsWith('{'))) {
          return [];
        }
        const parsed = JSON.parse(s);
        items = Array.isArray(parsed) ? parsed : [parsed];
      } else if (typeof toc === 'object') {
        const obj = toPlainObject(toc);
        if (!obj) {
          return [];
        }
        items = [obj];
      } else {
        return [];
      }

      return items.map((item, index) => normalizeTocItem(item, index)).filter(Boolean);
    } catch {
      return [];
    }
  };

  const tocItems = normalizeToc(tableOfContents);

  const tocItemsPlain = (() => {
    try {
      return typeof structuredClone === 'function' ? structuredClone(tocItems) : JSON.parse(JSON.stringify(tocItems));
    } catch {
      return Array.isArray(tocItems)
        ? tocItems.map(i => ({
            id: i?.id ?? '',
            label: i?.label ?? '',
            link: i?.link ?? '',
            scrollLink: i?.scrollLink ?? undefined,
          }))
        : [];
    }
  })();

  return (
    <>
      {showProgressBar && scrollProgress > 0 && (
        <div
          className={`fixed left-0 top-18 z-[998] h-2 w-full transition-opacity duration-500 xl:top-20 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="h-full bg-primary-1500 transition-all duration-700 ease-out"
            style={{ width: `${scrollProgress}%` }}
            aria-label={`Reading progress: ${Math.round(scrollProgress)}%`}
            role="progressbar"
            aria-valuenow={Math.round(scrollProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}

      <div
        ref={contentWrapperRef}
        className="relative"
        style={
          enableLockedContent && !isUnlocked && clipHeight
            ? {
                maxHeight: `${clipHeight}px`,
                overflow: 'hidden',
                marginBottom: '0',
                paddingBottom: '0',
              }
            : undefined
        }
      >
        <Section noDarkMode>
          <div className="grid w-full grid-cols-12 gap-6">
            <aside className="col-span-full contents h-full lg:col-span-3 lg:block">
              <div className="sticky contents h-fit w-full flex-col gap-10 lg:flex">
                <div className="col-span-full row-start-1">
                  <TableOfContents label={tocHeading} items={tocItemsPlain} activePath={activePath} />
                </div>

                {company?.value?.data && (
                  <div className="col-span-full row-start-2">
                    <CompanyCard {...company.value.data} />
                  </div>
                )}

                {showConversionPanel && conversionPanel?.value?.data && (
                  <div className="col-span-full row-start-4">
                    <ResourceConversionPanel
                      {...conversionPanel.value.data}
                      theme={resourceConversionPanelTheme || 'green'}
                    />
                  </div>
                )}
              </div>
            </aside>

            <article
              ref={articleRef}
              className="col-span-full flex flex-col gap-6 bg-primary-25 lg:col-span-8 lg:col-start-5"
            >
              {children}
            </article>

            {authorData && (
              <div ref={authorCardRef} className="col-span-full flex flex-col gap-6 lg:col-span-8 lg:col-start-5">
                <AuthorCard {...authorData} />
              </div>
            )}
          </div>
        </Section>
      </div>

      {enableLockedContent && !isUnlocked && showLockBanner && unlockForm && (
        <div ref={lockBannerRef}>
          <ContainedConversionPanel
            id="article-lock-banner"
            heading={lockHeading}
            body={lockDescription}
            useForm={true}
            form={unlockForm}
            onFormSuccess={handleUnlockCallback}
            nextComponentIsDark={true}
            lockedContentPageId={pageId}
            theme="teal"
          />
        </div>
      )}
    </>
  );
};

export default withChildren(Article);
